// src/pages/WritePost/index.jsx
import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Tag, message, Space, Typography, Divider } from 'antd';
import { SaveOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import './index.css';

const { TextArea } = Input;
const { Title, Text } = Typography;

const WritePost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [tags, setTags] = useState([]);
  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) {
      setIsEditing(true);
      const posts = JSON.parse(localStorage.getItem('blogPosts') || '[]');
      const post = posts.find(p => p.id === parseInt(id));
      
      if (post) {
        form.setFieldsValue({
          title: post.title,
          summary: post.summary,
          content: post.content,
        });
        setTags(post.tags || []);
      }
    }
  }, [id, form]);

  const handleClose = (removedTag) => {
    const newTags = tags.filter(tag => tag !== removedTag);
    setTags(newTags);
  };

  const showInput = () => {
    setInputVisible(true);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputConfirm = () => {
    if (inputValue && tags.indexOf(inputValue) === -1) {
      setTags([...tags, inputValue]);
    }
    setInputVisible(false);
    setInputValue('');
  };

  const onFinish = (values) => {
    if (tags.length === 0) {
      message.error('请至少添加一个标签');
      return;
    }

    setSaving(true);
    const posts = JSON.parse(localStorage.getItem('blogPosts') || '[]');
    const now = new Date();
    const formattedDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

    if (isEditing) {
      const updatedPosts = posts.map(post => 
        post.id === parseInt(id) 
          ? { ...post, ...values, tags, updatedAt: formattedDate }
          : post
      );
      localStorage.setItem('blogPosts', JSON.stringify(updatedPosts));
      message.success('文章更新成功！');
    } else {
      const newPost = {
        id: posts.length > 0 ? Math.max(...posts.map(p => p.id)) + 1 : 1,
        ...values,
        tags,
        author: localStorage.getItem('username') || '管理员',
        createdAt: formattedDate,
        updatedAt: formattedDate,
        viewCount: 0
      };
      
      localStorage.setItem('blogPosts', JSON.stringify([...posts, newPost]));
      message.success('文章发布成功！');
    }
    
    setTimeout(() => {
      setSaving(false);
      navigate('/home');
    }, 1000);
  };

  return (
    <div 
      className="write-post-page"
      style={{
        position: 'relative',
        minHeight: '100vh',
        zIndex: 1,
      }}
    >
      {/* 视频背景 */}
      <video
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: -1,
        }}
      >
        <source src={`${process.env.PUBLIC_URL}/原神-那维莱特.mp4`} type="video/mp4" />
        您的浏览器不支持视频播放。
      </video>
      
      {/* 主要内容 - 半透明效果 */}
      <div 
        className="page-content"
        style={{
          width: '100%',
          maxWidth: '95%',
          margin: '0 auto',
          padding: '20px 0 60px',
          minHeight: '100vh',
          backgroundColor: 'rgba(255, 255, 255, 0.65)', // ← 主背景透明度
          backdropFilter: 'blur(8px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div 
          className="page-header"
          style={{
            padding: '40px 40px 30px',
            textAlign: 'left',
            marginBottom: '30px',
          }}
        >
          <Title level={2} className="page-title">写文章</Title>
          <Text type="secondary" className="page-subtitle">分享您的知识与见解</Text>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="write-form"
          style={{
            padding: '0 40px',
          }}
        >
          {/* 标题 - 必填 */}
          <Form.Item
            name="title"
            rules={[{ required: true, message: '请输入文章标题' }]}
            className="form-item-title"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(5px)',
              padding: '20px',
              borderRadius: '8px',
            }}
          >
            <Input
              placeholder="请输入文章标题"
              size="large"
              className="title-input"
            />
          </Form.Item>

          {/* 摘要 - 必填 */}
          <Form.Item
            name="summary"
            label="摘要（必填，用于列表页面显示）"
            rules={[{ required: true, message: '请输入文章摘要' }]}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(5px)',
              padding: '20px',
              borderRadius: '8px',
              marginTop: '20px',
            }}
          >
            <TextArea 
              placeholder="请简要描述文章内容，吸引读者点击..." 
              rows={3}
              showCount 
              maxLength={200}
              className="summary-textarea"
            />
          </Form.Item>

          <Divider />

          {/* 标签管理 */}
          <div 
            className="tags-section"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(5px)',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '20px',
            }}
          >
            <div className="tags-header">
              标签管理（必填，请至少添加一个标签）
            </div>
            <div className="tags-container">
              {tags.map(tag => (
                <Tag
                  key={tag}
                  closable
                  onClose={() => handleClose(tag)}
                  className="post-tag"
                  style={{
                    backgroundColor: 'rgba(245, 245, 245, 0.9)',
                    border: '1px solid rgba(232, 232, 232, 0.7)',
                    color: '#555',
                  }}
                >
                  {tag}
                </Tag>
              ))}
              
              {inputVisible ? (
                <Input
                  type="text"
                  size="small"
                  className="tag-input"
                  value={inputValue}
                  onChange={handleInputChange}
                  onBlur={handleInputConfirm}
                  onPressEnter={handleInputConfirm}
                  autoFocus
                  placeholder="输入标签"
                />
              ) : (
                <Tag 
                  className="add-tag-btn" 
                  onClick={showInput}
                  icon={<PlusOutlined />}
                  style={{
                    backgroundColor: 'rgba(24, 144, 255, 0.1)',
                    border: '1px solid rgba(24, 144, 255, 0.3)',
                    color: '#1890ff',
                  }}
                >
                  添加标签
                </Tag>
              )}
            </div>
            <div className="tag-hint">
              例如：React、前端、教程、JavaScript
            </div>
          </div>

          <Divider />

          {/* 正文 - 必填 */}
          <Form.Item
            name="content"
            label="正文（必填）"
            rules={[{ required: true, message: '请输入正文内容' }]}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(5px)',
              padding: '20px',
              borderRadius: '8px',
              marginTop: '20px',
            }}
          >
            <TextArea 
              placeholder="请开始撰写您的文章..." 
              rows={16}
              showCount 
              maxLength={10000}
              className="content-textarea"
            />
          </Form.Item>

          {/* 操作按钮 */}
          <div 
            className="action-buttons"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(5px)',
              padding: '20px',
              borderRadius: '8px',
              marginTop: '20px',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<SaveOutlined />}
                size="large"
                loading={saving}
                style={{
                  backgroundColor: '#1890ff',
                  borderColor: '#1890ff',
                  boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
                }}
              >
                {isEditing ? '更新文章' : '发布文章'}
              </Button>
              <Button 
                icon={<CloseOutlined />}
                onClick={() => navigate('/')}
                size="large"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderColor: 'rgba(232, 232, 232, 0.7)',
                  color: '#666',
                }}
              >
                取消返回
              </Button>
            </Space>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default WritePost;